import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, IconButton, RadioGroup, FormControlLabel, Radio, Button, Fade, Alert, Chip } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

function Flashcard({ cards, onCorrectAnswer, onIncorrectAnswer, correctlyAnswered, activeCaseStudy, resetIndex }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showIncorrectFeedback, setShowIncorrectFeedback] = useState(false);

  useEffect(() => {
    setSelectedAnswer('');
    setIsAnswered(false);
    setIsCorrect(false);
    setShowIncorrectFeedback(false);
  }, [currentIndex]);

  // Reset to first question when case study changes
  useEffect(() => {
    setCurrentIndex(0);
    setSelectedAnswer('');
    setIsAnswered(false);
    setIsCorrect(false);
    setShowIncorrectFeedback(false);
  }, [activeCaseStudy]);

  // Reset index when requested
  useEffect(() => {
    if (resetIndex) {
      setCurrentIndex(0);
      setSelectedAnswer('');
      setIsAnswered(false);
      setIsCorrect(false);
      setShowIncorrectFeedback(false);
    }
  }, [resetIndex]);

  const handleNext = () => {
    // Only move to next question if there is one and it hasn't been answered yet
    const nextIndex = currentIndex + 1;
    if (nextIndex < cards.length) {
      setCurrentIndex(nextIndex);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleAnswerSubmit = () => {
    if (!selectedAnswer) return;
    
    const currentCard = cards[currentIndex];
    const isAnswerCorrect = typeof currentCard.correctAnswer === 'number' ? 
      parseInt(selectedAnswer) === currentCard.correctAnswer : 
      selectedAnswer === currentCard.correctAnswer;
    
    if (isAnswerCorrect) {
      setIsAnswered(true);
      setIsCorrect(true);
      onCorrectAnswer(currentIndex);
      // Only move to next question if there is one
      setTimeout(() => {
        if (currentIndex < cards.length - 1) {
          handleNext();
        }
      }, 2000);
    } else {
      onIncorrectAnswer();
      setShowIncorrectFeedback(true);
      setTimeout(() => {
        setShowIncorrectFeedback(false);
        setSelectedAnswer('');
      }, 1500);
    }
  };

  const handleAnswerChange = (event) => {
    setSelectedAnswer(event.target.value);
  };

  // Check if the current card has been answered correctly
  const isCurrentCardAnswered = () => {
    if (!correctlyAnswered) return false;
    const prefixedIndex = `${activeCaseStudy}-${currentIndex}`;
    return correctlyAnswered.has(prefixedIndex);
  };

  if (!cards || !cards[currentIndex]) {
    return <Typography>No flashcards available</Typography>;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">
              Question {currentIndex + 1} of {cards.length}
            </Typography>
            {cards[currentIndex].relatedStep !== undefined && (
              <Chip 
                label={`Protocol Step ${cards[currentIndex].relatedStep + 1}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
          
          <Typography variant="h6" component="div">
            {cards[currentIndex].question}
          </Typography>

          <RadioGroup
            value={selectedAnswer}
            onChange={handleAnswerChange}
            sx={{ mb: 2 }}
          >
            {cards[currentIndex].options.map((option, idx) => (
              <FormControlLabel
                key={idx}
                value={typeof cards[currentIndex].correctAnswer === 'number' ? idx.toString() : option}
                control={<Radio />}
                label={option}
                disabled={isAnswered}
                sx={{
                  mb: 1,
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: isAnswered && ((typeof cards[currentIndex].correctAnswer === 'number' && parseInt(selectedAnswer) === idx) || 
                    (typeof cards[currentIndex].correctAnswer === 'string' && option === cards[currentIndex].correctAnswer)) ? 
                    'success.light' : 'transparent',
                  '&:hover': {
                    backgroundColor: isAnswered ? 
                      ((typeof cards[currentIndex].correctAnswer === 'number' && parseInt(selectedAnswer) === idx) || 
                       (typeof cards[currentIndex].correctAnswer === 'string' && option === cards[currentIndex].correctAnswer)) ? 
                      'success.light' : 'transparent' : 'action.hover',
                  },
                }}
              />
            ))}
          </RadioGroup>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            position: 'sticky',
            bottom: 16,
            zIndex: 1,
            backgroundColor: 'background.paper',
            py: 2
          }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAnswerSubmit}
              disabled={!selectedAnswer || isAnswered}
              size="large"
              sx={{
                minWidth: '200px',
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 5,
                },
              }}
            >
              Submit Answer
            </Button>
          </Box>

          <Fade in={showIncorrectFeedback}>
            <Alert 
              severity="error" 
              sx={{ 
                position: 'absolute',
                bottom: 80,
                left: 16,
                right: 16,
              }}
            >
              Incorrect answer. Try again!
            </Alert>
          </Fade>

          {isCorrect && (
            <Box sx={{ mt: 2 }}>
              <Alert 
                severity="success"
                icon={<CheckCircleOutlineIcon />}
                sx={{ mb: 2 }}
              >
                Correct answer! Moving to next question in 4 seconds...
              </Alert>
              
              <Box sx={{ 
                bgcolor: 'success.light', 
                p: 2, 
                borderRadius: 1,
                border: 1,
                borderColor: 'success.main'
              }}>
                <Typography variant="subtitle1" color="success.dark" gutterBottom>
                  Explanation:
                </Typography>
                <Typography variant="body2" color="success.dark">
                  {cards[currentIndex].explanation}
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <IconButton onClick={handlePrevious} color="primary" disabled={isCorrect}>
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton onClick={handleNext} color="primary" disabled={isCorrect}>
            <NavigateNextIcon />
          </IconButton>
        </Box>
      </Card>
    </Box>
  );
}

export default Flashcard;
